"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cx } from "../cx.ts";
import { Icon } from "./icon.tsx";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "danger-ghost" | "outline";
type Size = "sm" | "md";

const VARIANTS: Record<Variant, string> = {
  primary: "bg-primary text-ink-on-primary hover:bg-primary-hover",
  secondary: "bg-surface text-ink-body border border-line hover:bg-surface-subtle",
  ghost: "text-ink-muted hover:bg-surface-subtle hover:text-ink-body",
  // A hover colour rather than `hover:opacity-90`, which faded the label along
  // with the fill and made the most consequential button in the product the
  // one that dimmed when you reached for it.
  danger: "bg-danger text-ink-on-primary hover:bg-danger-hover",
  /**
   * The quiet destructive — a delete sitting in a footer, where a filled one
   * would pull the eye to the button nobody should reach for by accident.
   *
   * It is a variant rather than `variant="ghost" className="text-danger"`,
   * which is what three call sites did and what none of them got: `cx`
   * concatenates and does not merge, so `ghost`'s own `text-ink-muted` and the
   * caller's `text-danger` both landed and the cascade picked between them by
   * the order Tailwind happened to emit them in. It picked `text-ink-muted`,
   * and every delete in both consoles has been grey.
   */
  "danger-ghost": "text-danger hover:bg-danger-soft",
  // Terracotta on white: a secondary action that is still the brand's, used
  // where a filled button would compete with the page's primary CTA.
  outline: "border border-primary bg-surface text-primary hover:bg-primary-soft",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  /**
   * Shows a spinner and disables the control. The label stays put: swapping it
   * for a spinner resizes the button mid-click, and it resizes further in
   * Khmer, where the label it replaced was ~30% wider.
   */
  loading?: boolean;
  children: ReactNode;
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-control font-normal",
        "transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2",
        "focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50",
        // No fixed width, and `whitespace-nowrap` is deliberately absent: a
        // Khmer label runs ~30% longer than its English draft and must be
        // allowed to size the control rather than overflow it.
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Icon name="spinner" size={16} className="motion-safe:animate-spin" /> : null}
      {children}
    </button>
  );
}
